import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

export const ReservationsCheckPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [cancellingEndIdx, setCancellingEndIdx] = useState<string | null>(null);

  const { data: externalData, isLoading: isExternalLoading, error: externalError, refetch: refetchExternal } = useQuery({
    queryKey: ['externalReservations', customerName, customerPhone],
    queryFn: async () => {
      if (!customerPhone) {
        throw new Error('연락처는 필수입니다.');
      }
      const params = new URLSearchParams();
      if (customerName) params.append('customer_name', customerName);
      params.append('customer_phone', customerPhone);

      const response = await apiClient.post<any>(`/reservations/check?${params.toString()}`);
      return response.data;
    },
    enabled: false,
  });

  // 예약 취소 mutation
  const cancelMutation = useMutation({
    mutationFn: async (endIdx: string) => {
      setCancellingEndIdx(endIdx);
      const response = await apiClient.post(`/reservations/cancel-public/${endIdx}`);
      return response.data;
    },
    onSuccess: () => {
      setCancellingEndIdx(null);
      alert('예약이 성공적으로 취소되었습니다.');
      refetchExternal(); // 목록 새로고침
    },
    onError: (error: any) => {
      setCancellingEndIdx(null);
      const errorMessage = error?.response?.data?.detail || '예약 취소에 실패했습니다.';
      alert(errorMessage);
    },
  });

  // URL 파라미터에서 이름과 연락처를 가져와서 자동으로 조회
  useEffect(() => {
    const name = searchParams.get('name');
    const phone = searchParams.get('phone');

    if (name) {
      setCustomerName(name);
    }
    if (phone) {
      setCustomerPhone(phone);
    }

    // 두 값이 모두 있으면 자동으로 조회
    if (name && phone) {
      setTimeout(() => {
        refetchExternal();
      }, 100);
    }
  }, [searchParams]);

  const handleExternalSearch = () => {
    if (!customerName) {
      alert('고객명은 필수입니다.');
      return;
    }
    if (!customerPhone) {
      alert('연락처는 필수입니다.');
      return;
    }
    refetchExternal();
  };

  const handleCancelReservation = (reservation: any) => {
    const endIdx = reservation.end_idx;

    if (!endIdx) {
      alert('예약 번호를 찾을 수 없습니다.');
      return;
    }

    if (window.confirm(`정말로 이 예약을 취소하시겠습니까?\n\n예약번호: ${endIdx}\n차량: ${reservation.carname}\n대여일: ${reservation.startdate}`)) {
      cancelMutation.mutate(endIdx);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleExternalSearch();
    }
  };

  const externalReservations = externalData?.data || [];

  return (
    <Layout>
      <div className="px-2 py-4">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">예약 확인</h1>

        {/* Search Form */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                고객명 (필수)
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                onKeyDown={handleKeyDown}
                className="block w-48 px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                placeholder="홍길동"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                연락처 (필수)
              </label>
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => {
                  // 숫자만 추출
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  // 자동으로 하이픈 추가 (010-1234-5678 형식)
                  let formatted = value;
                  if (value.length > 3 && value.length <= 7) {
                    formatted = value.slice(0, 3) + '-' + value.slice(3);
                  } else if (value.length > 7) {
                    formatted = value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
                  }
                  setCustomerPhone(formatted);
                }}
                onKeyDown={handleKeyDown}
                className="block w-48 px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                placeholder="010-1234-5678"
                maxLength={13}
              />
            </div>
            <button
              onClick={handleExternalSearch}
              className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
            >
              조회
            </button>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">검색 결과</h2>
          </div>

          {isExternalLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">검색 중...</p>
            </div>
          ) : externalError ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">검색에 실패했습니다.</p>
              <p className="text-sm text-gray-500 mt-2">
                {(externalError as any)?.response?.data?.detail || (externalError as Error).message}
              </p>
            </div>
          ) : !externalData ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p className="mt-2 text-slate-500 text-sm">고객명(필수)과 연락처(필수)로 예약을 조회하세요.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {externalReservations.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        예약번호
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        차량명
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        차량등급
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        연료
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        대여상태
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        대여일
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        반납예정일
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        예약일시
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                        액션
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {externalReservations.map((item: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {item.end_idx || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {item.carname || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {item.carClassname || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {item.fuel || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded ${
                            item.rentstate === '대여완료'
                              ? 'bg-green-100 text-green-800'
                              : item.rentstate === '예약'
                              ? 'bg-blue-100 text-blue-800'
                              : item.rentstate === '예약중'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.rentstate || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {item.startdate ? `${item.startdate.slice(0, 4)}-${item.startdate.slice(4, 6)}-${item.startdate.slice(6, 8)}` : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {item.yenddate ? `${item.yenddate.slice(0, 4)}-${item.yenddate.slice(4, 6)}-${item.yenddate.slice(6, 8)}` : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {item.reservationDate || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {item.rentstate === '예약' && (
                            <button
                              onClick={() => handleCancelReservation(item)}
                              disabled={cancellingEndIdx === item.end_idx}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                              {cancellingEndIdx === item.end_idx ? '처리중...' : '취소'}
                            </button>
                          )}
                          {item.rentstate !== '예약' && (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-6 py-12 text-center">
                  <p className="text-slate-500 text-sm">검색 결과가 없습니다.</p>
                  {externalData && externalData.code !== "0" && (
                    <div className="mt-4 p-4 bg-yellow-50 rounded max-w-2xl mx-auto">
                      <p className="text-sm text-yellow-800">
                        <strong>외부 API 메시지:</strong> {externalData.msg}
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        (응답 코드: {externalData.code})
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
